import { useQuery, type QueryKey } from '@tanstack/react-query';
import service from '../service';
import type { AiChat, AiChatDetail, AiSuggestion, BusinessObjectMap } from '@Types/soul';

export const aiChatKeys = {
  all: ['ai-chats'] as const,
  list: (params: { limit?: number; cursor?: string }) =>
    ['ai-chats', params] as const,
  detail: (id: string) => ['ai-chat', id] as const,
  businessObjects: (id: string) => ['ai-chat-bos', id] as const,
  starterSuggestions: (soulId: string) =>
    ['ai-starter-suggestions', soulId] as const,
  followUpSuggestions: (chatId: string, messageId: string) =>
    ['ai-followup-suggestions', chatId, messageId] as const,
};

export function useAiChats({
  limit,
  cursor,
  enabled = true,
}: {
  limit?: number;
  cursor?: string;
  enabled?: boolean;
} = {}) {
  return useQuery<AiChat[]>({
    queryKey: aiChatKeys.list({ limit, cursor }) as unknown as QueryKey,
    queryFn: () => service.getAiChats({ limit, cursor }),
    enabled,
  });
}

export function useAiChat({
  chatId,
  enabled = true,
}: {
  chatId?: string;
  enabled?: boolean;
}) {
  return useQuery<AiChatDetail>({
    queryKey: aiChatKeys.detail(chatId!) as unknown as QueryKey,
    queryFn: () => service.getAiChat(chatId!),
    enabled: enabled && !!chatId,
  });
}

// Starter prompt pills for an empty chat. Keyed on the active Soul and cached
// for the session since starter suggestions don't change turn-to-turn.
export function useAiStarterSuggestions({
  soulId,
  enabled = true,
}: {
  soulId?: string;
  enabled?: boolean;
}) {
  return useQuery<AiSuggestion[]>({
    queryKey: aiChatKeys.starterSuggestions(soulId ?? '') as unknown as QueryKey,
    queryFn: () => service.getAiSuggestions(soulId!),
    enabled: enabled && !!soulId,
    staleTime: 1000 * 60 * 30,
  });
}

// Follow-up prompt pills shown after an assistant turn. Keyed on the chat plus
// the last assistant message, with staleTime Infinity, so suggestions are
// generated once per turn and cached until a new message produces a new reply.
export function useAiFollowUpSuggestions({
  soulId,
  chatId,
  lastAssistantMessageId,
  enabled = true,
}: {
  soulId?: string;
  chatId?: string;
  lastAssistantMessageId?: string;
  enabled?: boolean;
}) {
  return useQuery<AiSuggestion[]>({
    queryKey: aiChatKeys.followUpSuggestions(
      chatId ?? '',
      lastAssistantMessageId ?? '',
    ) as unknown as QueryKey,
    queryFn: () => service.getAiSuggestions(soulId!, chatId!),
    enabled:
      enabled && !!soulId && !!chatId && !!lastAssistantMessageId,
    staleTime: Infinity,
  });
}

export function useBusinessObjects({
  chatId,
  enabled = true,
}: {
  chatId?: string;
  enabled?: boolean;
}) {
  return useQuery<BusinessObjectMap>({
    queryKey: aiChatKeys.businessObjects(chatId!) as unknown as QueryKey,
    queryFn: () => service.getBusinessObjects(chatId!),
    enabled: enabled && !!chatId,
  });
}
